import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, Lock, User, Phone, Calendar, ArrowLeft } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { countries, colombiaData, departments } from '../data/countries';
import { SECTORES_ECONOMICOS, SECTORES_LIST, VULNERABLE_POPULATION_TYPES, calculateAge } from '../data/sectoresEconomicos';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RegisterNatural({ onBack, darkMode = false }) {
  // Clases para inputs en modo oscuro
  const inputClass = `h-11 rounded-sm ${darkMode ? 'bg-[#1a1a1a] text-white border-gray-700 placeholder:text-gray-500' : ''}`;
  const selectTriggerClass = `h-11 rounded-sm ${darkMode ? 'bg-[#1a1a1a] text-white border-gray-700' : ''}`;
  const labelClass = `text-sm font-medium ${darkMode ? 'text-gray-300' : ''}`;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    user_type: 'natural',
    document_type: '',
    document_id: '',
    verification_digit: '',
    first_name: '',
    second_name: '',
    first_lastname: '',
    second_lastname: '',
    has_email: true,
    email: '',
    phone: '',
    address: '',
    neighborhood: '',
    biological_sex: '',
    role: '',
    position: '',
    is_migrant: false,
    migrant_country: '',
    is_lgbtiq: '',
    is_caregiver: false,
    is_veteran: false,
    residence_country: '',
    residence_department: '',
    residence_municipality: '',
    nationality: '',
    birth_date: '',
    is_youth_14_28: false,
    is_senior_60_plus: false,
    economic_activity: '',
    main_product: '',
    economic_sector: '',
    economic_subsector: '',
    location_type: '',
    has_rut: false,
    years_formalization: '',
    num_employees: '',
    monthly_sales_intervention: '',
    total_assets_last_period: '',
    non_profit: false,
    has_website: false,
    website_url: '',
    ruv_number: '',
    vulnerable_population_type: '',
    company_size: '',
    entrepreneurship_stage: '',
    education_level: '',
    ethnic_group: '',
    interest_area: '',
    interests: [],
    vulnerable_population: false,
    victim_ruv: false,
    has_disability: false,
    disability_type: '',
    in_minminas_census: false,
    data_authorization: false,
    password: ''
  });

  // Calcular edad automáticamente
  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    const age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      return age - 1;
    }
    return age;
  };

  const handleBirthDateChange = (date) => {
    setFormData({ ...formData, birth_date: date });
    
    const age = calculateAge(date);
    if (age !== null) {
      setFormData(prev => ({
        ...prev,
        birth_date: date,
        is_youth_14_28: age >= 14 && age <= 28,
        is_senior_60_plus: age >= 60
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.data_authorization) {
      toast.error('Debes aceptar la autorización de tratamiento de datos personales');
      return;
    }
    
    setLoading(true);

    try {
      await axios.post(`${API}/auth/register`, formData);
      toast.success('¡Registro exitoso! Revisa tu correo para verificar tu cuenta.');
      navigate('/verify', { state: { email: formData.email } });
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al registrar';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* Botón Ir Atrás */}
      <button
        type="button"
        onClick={onBack}
        className={`flex items-center gap-2 text-sm font-medium mb-6 transition-colors ${
          darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'
        }`}
      >
        <ArrowLeft className="w-4 h-4" />
        Volver a selección de tipo
      </button>

      <form onSubmit={handleSubmit} className="space-y-6">

      {/* Datos de Identificación */}
      <div className="space-y-4">
        <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Datos de Identificación</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="document_type" className={labelClass}>
              Tipo de Identificación *
            </Label>
            <Select
              value={formData.document_type}
              onValueChange={(value) => setFormData({ ...formData, document_type: value })}
              required
            >
              <SelectTrigger className={selectTriggerClass} data-testid="register-document-type">
                <SelectValue placeholder="Seleccione..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="CC">Cédula de ciudadanía</SelectItem>
                <SelectItem value="PAS">Pasaporte</SelectItem>
                <SelectItem value="NUIP">NUIP</SelectItem>
                <SelectItem value="TI">Tarjeta de identidad</SelectItem>
                <SelectItem value="CE">Cédula Extranjería</SelectItem>
                <SelectItem value="PPT">Permiso por protección temporal</SelectItem>
                <SelectItem value="PEP">Permiso especial de Permanencia</SelectItem>
                <SelectItem value="DOP">Documento de otro país</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="document_id" className={labelClass}>
              Número de Identificación *
            </Label>
            <Input
              id="document_id"
              type="text"
              placeholder="123456789"
              value={formData.document_id}
              onChange={(e) => setFormData({ ...formData, document_id: e.target.value })}
              className={inputClass}
              required
              data-testid="register-document-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="verification_digit" className={labelClass}>
              Dígito de Verificación
            </Label>
            <Input
              id="verification_digit"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={1}
              placeholder="0"
              value={formData.verification_digit}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 1);
                setFormData({ ...formData, verification_digit: val });
              }}
              className={inputClass}
              data-testid="register-verification-digit-input"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="first_name" className={labelClass}>
              Primer Nombre *
            </Label>
            <Input
              id="first_name"
              type="text"
              placeholder="Juan"
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              className={inputClass}
              required
              data-testid="register-firstname-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="second_name" className={labelClass}>
              Segundo Nombre
            </Label>
            <Input
              id="second_name"
              type="text"
              placeholder="Carlos"
              value={formData.second_name}
              onChange={(e) => setFormData({ ...formData, second_name: e.target.value })}
              className={inputClass}
              data-testid="register-secondname-input"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="first_lastname" className={labelClass}>
              Primer Apellido *
            </Label>
            <Input
              id="first_lastname"
              type="text"
              placeholder="Pérez"
              value={formData.first_lastname}
              onChange={(e) => setFormData({ ...formData, first_lastname: e.target.value })}
              className={inputClass}
              required
              data-testid="register-firstlastname-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="second_lastname" className={labelClass}>
              Segundo Apellido
            </Label>
            <Input
              id="second_lastname"
              type="text"
              placeholder="García"
              value={formData.second_lastname}
              onChange={(e) => setFormData({ ...formData, second_lastname: e.target.value })}
              className={inputClass}
              data-testid="register-secondlastname-input"
            />
          </div>
        </div>
      </div>

      {/* Datos de Contacto */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-primary">Datos de Contacto</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="email" className={labelClass}>
              Correo Electrónico *
            </Label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                placeholder="tu@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="pl-10 h-11 rounded-sm"
                required
                data-testid="register-email-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone" className={labelClass}>
              Teléfono Celular (10 dígitos) *
            </Label>
            <div className="relative">
              <Phone className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
              <Input
                id="phone"
                type="tel"
                placeholder="3001234567"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="pl-10 h-11 rounded-sm"
                required
                maxLength={10}
                pattern="[0-9]{10}"
                data-testid="register-phone-input"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="address" className={labelClass}>
              Dirección
            </Label>
            <Input
              id="address"
              type="text"
              placeholder="Calle 123 # 45-67"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className={inputClass}
              data-testid="register-address-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="neighborhood" className={labelClass}>
              Barrio
            </Label>
            <Input
              id="neighborhood"
              type="text"
              placeholder="Ej: Chapinero"
              value={formData.neighborhood}
              onChange={(e) => setFormData({ ...formData, neighborhood: e.target.value })}
              className={inputClass}
              data-testid="register-neighborhood-input"
            />
          </div>
        </div>
      </div>

      {/* Información Demográfica */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-primary">Información Demográfica</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="biological_sex" className={labelClass}>
              Sexo Biológico *
            </Label>
            <Select
              value={formData.biological_sex}
              onValueChange={(value) => setFormData({ ...formData, biological_sex: value })}
              required
            >
              <SelectTrigger className={selectTriggerClass}>
                <SelectValue placeholder="Seleccione..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hombre">Hombre</SelectItem>
                <SelectItem value="mujer">Mujer</SelectItem>
                <SelectItem value="intersexual">Intersexual</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="birth_date" className={labelClass}>
              Fecha de Nacimiento *
            </Label>
            <Input
              id="birth_date"
              type="date"
              value={formData.birth_date}
              onChange={(e) => handleBirthDateChange(e.target.value)}
              className={inputClass}
              required
              data-testid="register-birthdate-input"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="calculated_age" className={labelClass}>
              Edad
            </Label>
            <Input
              id="calculated_age"
              type="text"
              value={calculateAge(formData.birth_date) !== '' ? `${calculateAge(formData.birth_date)} años` : ''}
              readOnly
              placeholder="Se calcula desde la fecha"
              className={`${inputClass} bg-slate-50`}
              data-testid="register-calculated-age"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="residence_country" className={labelClass}>
              País de Residencia *
            </Label>
            <Select
              value={formData.residence_country}
              onValueChange={(value) => setFormData({ 
                ...formData, 
                residence_country: value,
                residence_department: value === 'Colombia' ? formData.residence_department : '',
                residence_municipality: value === 'Colombia' ? formData.residence_municipality : ''
              })}
              required
            >
              <SelectTrigger className={selectTriggerClass}>
                <SelectValue placeholder="Seleccione país..." />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {countries.map((country) => (
                  <SelectItem key={country} value={country}>
                    {country}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="nationality" className={labelClass}>
              Nacionalidad *
            </Label>
            <Select
              value={formData.nationality}
              onValueChange={(value) => setFormData({ ...formData, nationality: value })}
              required
            >
              <SelectTrigger className={selectTriggerClass}>
                <SelectValue placeholder="Seleccione nacionalidad..." />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {countries.map((country) => (
                  <SelectItem key={country} value={country}>
                    {country}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Departamentos y Municipios - Solo si es Colombia */}
        {formData.residence_country === 'Colombia' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="residence_department" className={labelClass}>
                Departamento *
              </Label>
              <Select
                value={formData.residence_department}
                onValueChange={(value) => setFormData({ 
                  ...formData, 
                  residence_department: value,
                  residence_municipality: '' // Reset municipality when department changes
                })}
                required
              >
                <SelectTrigger className={selectTriggerClass}>
                  <SelectValue placeholder="Seleccione departamento..." />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {departments.map((dept) => (
                    <SelectItem key={dept} value={dept}>
                      {dept}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="residence_municipality" className={labelClass}>
                Municipio *
              </Label>
              <Select
                value={formData.residence_municipality}
                onValueChange={(value) => setFormData({ ...formData, residence_municipality: value })}
                disabled={!formData.residence_department}
                required
              >
                <SelectTrigger className={selectTriggerClass}>
                  <SelectValue placeholder={formData.residence_department ? "Seleccione municipio..." : "Primero seleccione departamento"} />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {formData.residence_department && colombiaData[formData.residence_department]?.map((muni) => (
                    <SelectItem key={muni} value={muni}>
                      {muni}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="ethnic_group" className={labelClass}>
            Grupo Étnico
          </Label>
          <Select
            value={formData.ethnic_group}
            onValueChange={(value) => setFormData({ ...formData, ethnic_group: value })}
          >
            <SelectTrigger className={selectTriggerClass} data-testid="register-ethnic-group-select">
              <SelectValue placeholder="Seleccione..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="negro">Negro</SelectItem>
              <SelectItem value="afro">Afro</SelectItem>
              <SelectItem value="raizal">Raizal</SelectItem>
              <SelectItem value="palenquero">Palenquero</SelectItem>
              <SelectItem value="indigena">Indígena</SelectItem>
              <SelectItem value="rrom_gitano">Rrom o Gitano</SelectItem>
              <SelectItem value="ninguno">Ninguno</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Información Profesional */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-primary">Información Profesional</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="role" className={labelClass}>
              Rol de la Persona Asociada
            </Label>
            <Select
              value={formData.role}
              onValueChange={(value) => setFormData({ ...formData, role: value })}
            >
              <SelectTrigger className={selectTriggerClass}>
                <SelectValue placeholder="Seleccione..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="emprendedor">Emprendedor</SelectItem>
                <SelectItem value="empresario">Empresario</SelectItem>
                <SelectItem value="estudiante">Estudiante</SelectItem>
                <SelectItem value="docente">Docente</SelectItem>
                <SelectItem value="academia_universidad">Academia – universidad</SelectItem>
                <SelectItem value="aliado_ecosistema">Aliado del Ecosistema</SelectItem>
                <SelectItem value="aliado_gobierno">Aliado del Gobierno</SelectItem>
                <SelectItem value="gremio">Gremio</SelectItem>
                <SelectItem value="otro">Otro</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="position" className={labelClass}>
              Cargo de la Persona
            </Label>
            <Input
              id="position"
              type="text"
              placeholder="Gerente, Director, etc."
              value={formData.position}
              onChange={(e) => setFormData({ ...formData, position: e.target.value })}
              className={inputClass}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="education_level" className={labelClass}>
              Nivel Educativo
            </Label>
            <Select
              value={formData.education_level}
              onValueChange={(value) => setFormData({ ...formData, education_level: value })}
            >
              <SelectTrigger className={selectTriggerClass}>
                <SelectValue placeholder="Seleccione..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="primaria">Primaria</SelectItem>
                <SelectItem value="bachillerato">Bachillerato</SelectItem>
                <SelectItem value="tecnico">Técnico</SelectItem>
                <SelectItem value="tecnologo">Tecnólogo</SelectItem>
                <SelectItem value="profesional">Profesional</SelectItem>
                <SelectItem value="posgrado">Posgrado</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="economic_activity" className={labelClass}>
              Actividad Económica
            </Label>
            <Select
              value={formData.economic_activity}
              onValueChange={(value) => setFormData({ ...formData, economic_activity: value })}
            >
              <SelectTrigger className={selectTriggerClass}>
                <SelectValue placeholder="Seleccione..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="alojamiento_comidas">Actividades de alojamiento y servicios de comidas</SelectItem>
                <SelectItem value="salud_asistencia">Actividades de atención de la salud humana y de asistencia social</SelectItem>
                <SelectItem value="hogares_empleadores">Actividades de los hogares como empleadores; actividades no diferenciadas de los hogares como productores de bienes y servicios para uso propio</SelectItem>
                <SelectItem value="organizaciones_extraterritoriales">Actividades de organizaciones y órganos extraterritoriales</SelectItem>
                <SelectItem value="servicios_administrativos">Actividades de servicios administrativos de apoyo</SelectItem>
                <SelectItem value="financieras_seguros">Actividades financieras y de seguros</SelectItem>
                <SelectItem value="inmobiliarias">Actividades inmobiliarias</SelectItem>
                <SelectItem value="profesionales_cientificas">Actividades profesionales, científicas y técnicas</SelectItem>
                <SelectItem value="administracion_publica">Administración pública y defensa, planes de seguridad social de afiliación obligatoria</SelectItem>
                <SelectItem value="agricultura_ganaderia">Agricultura, ganadería, silvicultura y pesca</SelectItem>
                <SelectItem value="artes_entretenimiento">Artes, entrenamiento y recreación</SelectItem>
                <SelectItem value="comercio_mayor_menor">Comercio al por mayor y al por menor, reparación de vehículos automotores y motocicletas</SelectItem>
                <SelectItem value="construccion">Construcción</SelectItem>
                <SelectItem value="distribucion_agua">Distribución de agua, alcantarillado, gestión de desechos y actividades de saneamiento</SelectItem>
                <SelectItem value="ensenanza">Enseñanza</SelectItem>
                <SelectItem value="explotacion_minas">Explotación de minas y canteras</SelectItem>
                <SelectItem value="industrias_manufacturas">Industrias manufacturas</SelectItem>
                <SelectItem value="informacion_comunicacion">Información y comunicación</SelectItem>
                <SelectItem value="otras_actividades">Otras actividades de servicios</SelectItem>
                <SelectItem value="suministro_electricidad">Suministro de electricidad, gas, vapor y aire acondicionado</SelectItem>
                <SelectItem value="transporte_almacenamiento">Transporte y almacenamiento</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="company_size" className={labelClass}>
              Tamaño de la Empresa
            </Label>
            <Select
              value={formData.company_size}
              onValueChange={(value) => setFormData({ ...formData, company_size: value })}
            >
              <SelectTrigger className={selectTriggerClass}>
                <SelectValue placeholder="Seleccione..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="micro">Microempresa</SelectItem>
                <SelectItem value="pequena">Pequeña</SelectItem>
                <SelectItem value="mediana">Mediana</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="entrepreneurship_stage" className={labelClass}>
              Etapa del Proceso Emprendedor
            </Label>
            <Select
              value={formData.entrepreneurship_stage}
              onValueChange={(value) => setFormData({ ...formData, entrepreneurship_stage: value })}
            >
              <SelectTrigger className={selectTriggerClass}>
                <SelectValue placeholder="Seleccione..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="idea">Idea</SelectItem>
                <SelectItem value="validacion">Validación</SelectItem>
                <SelectItem value="temprana">Temprana</SelectItem>
                <SelectItem value="crecimiento">Crecimiento</SelectItem>
                <SelectItem value="expansion">Expansión</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Producto y Sector Económico */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-primary">Producto y Sector Económico</h3>
        
        <div className="space-y-2">
          <Label htmlFor="main_product" className={labelClass}>
            Producto Principal (Bien/Servicio)
          </Label>
          <Input
            id="main_product"
            type="text"
            placeholder="Ej: Servicios de consultoría tecnológica"
            value={formData.main_product}
            onChange={(e) => setFormData({ ...formData, main_product: e.target.value })}
            className={inputClass}
            data-testid="register-main-product-input"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="economic_sector" className={labelClass}>
              Sector Económico
            </Label>
            <Select
              value={formData.economic_sector}
              onValueChange={(value) => setFormData({ ...formData, economic_sector: value, economic_subsector: '' })}
            >
              <SelectTrigger className={selectTriggerClass} data-testid="register-economic-sector-select">
                <SelectValue placeholder="Seleccione..." />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {SECTORES_LIST.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="economic_subsector" className={labelClass}>
              Subsector Económico
            </Label>
            <Select
              value={formData.economic_subsector}
              onValueChange={(value) => setFormData({ ...formData, economic_subsector: value })}
              disabled={!formData.economic_sector}
            >
              <SelectTrigger className={selectTriggerClass} data-testid="register-economic-subsector-select">
                <SelectValue placeholder={formData.economic_sector ? "Seleccione..." : "Seleccione un sector primero"} />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {(SECTORES_ECONOMICOS[formData.economic_sector] || []).map((sub) => (
                  <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Información Empresarial Adicional */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-primary">Información Empresarial</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="location_type" className={labelClass}>
              Ubicación Geográfica
            </Label>
            <Select
              value={formData.location_type}
              onValueChange={(value) => setFormData({ ...formData, location_type: value })}
            >
              <SelectTrigger className={selectTriggerClass} data-testid="register-location-type-select">
                <SelectValue placeholder="Seleccione..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="urbano">Urbano</SelectItem>
                <SelectItem value="rural">Rural</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className={labelClass}>¿Tiene RUT?</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={formData.has_rut === false ? "default" : "outline"}
                className={`flex-1 h-11 rounded-sm ${formData.has_rut === false ? 'bg-primary text-primary-foreground' : ''}`}
                onClick={() => setFormData({ ...formData, has_rut: false })}
                data-testid="has-rut-no"
              >
                No
              </Button>
              <Button
                type="button"
                variant={formData.has_rut === true ? "default" : "outline"}
                className={`flex-1 h-11 rounded-sm ${formData.has_rut === true ? 'bg-primary text-primary-foreground' : ''}`}
                onClick={() => setFormData({ ...formData, has_rut: true })}
                data-testid="has-rut-yes"
              >
                Sí
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="years_formalization" className={labelClass}>Años de formalización</Label>
            <Input
              id="years_formalization"
              type="number"
              min="0"
              step="0.5"
              placeholder="0"
              value={formData.years_formalization}
              onChange={(e) => setFormData({ ...formData, years_formalization: e.target.value })}
              className={inputClass}
              data-testid="years-formalization-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="num_employees" className={labelClass}>Número de empleados</Label>
            <Input
              id="num_employees"
              type="number"
              min="0"
              placeholder="0"
              value={formData.num_employees}
              onChange={(e) => setFormData({ ...formData, num_employees: e.target.value })}
              className={inputClass}
              data-testid="num-employees-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ruv_number" className={labelClass}>Número RUV (si aplica)</Label>
            <Input
              id="ruv_number"
              type="text"
              placeholder="Opcional"
              value={formData.ruv_number}
              onChange={(e) => setFormData({ ...formData, ruv_number: e.target.value })}
              className={inputClass}
              data-testid="ruv-number-input"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="monthly_sales_intervention" className={labelClass}>Ventas mensuales al inicio de la intervención (COP)</Label>
            <Input
              id="monthly_sales_intervention"
              type="number"
              min="0"
              placeholder="0"
              value={formData.monthly_sales_intervention}
              onChange={(e) => setFormData({ ...formData, monthly_sales_intervention: e.target.value })}
              className={inputClass}
              data-testid="monthly-sales-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="total_assets_last_period" className={labelClass}>Valor total de activos último periodo (COP)</Label>
            <Input
              id="total_assets_last_period"
              type="number"
              min="0"
              placeholder="0"
              value={formData.total_assets_last_period}
              onChange={(e) => setFormData({ ...formData, total_assets_last_period: e.target.value })}
              className={inputClass}
              data-testid="total-assets-input"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className={labelClass}>¿Con ánimo de lucro?</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={formData.non_profit === true ? "default" : "outline"}
                className={`flex-1 h-11 rounded-sm ${formData.non_profit === true ? 'bg-primary text-primary-foreground' : ''}`}
                onClick={() => setFormData({ ...formData, non_profit: true })}
                data-testid="non-profit-yes"
              >
                Sin ánimo de lucro
              </Button>
              <Button
                type="button"
                variant={formData.non_profit === false ? "default" : "outline"}
                className={`flex-1 h-11 rounded-sm ${formData.non_profit === false ? 'bg-primary text-primary-foreground' : ''}`}
                onClick={() => setFormData({ ...formData, non_profit: false })}
                data-testid="non-profit-no"
              >
                Con ánimo de lucro
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label className={labelClass}>¿Tiene página web?</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={formData.has_website === false ? "default" : "outline"}
                className={`flex-1 h-11 rounded-sm ${formData.has_website === false ? 'bg-primary text-primary-foreground' : ''}`}
                onClick={() => setFormData({ ...formData, has_website: false, website_url: '' })}
                data-testid="natural-has-website-no"
              >
                No
              </Button>
              <Button
                type="button"
                variant={formData.has_website === true ? "default" : "outline"}
                className={`flex-1 h-11 rounded-sm ${formData.has_website === true ? 'bg-primary text-primary-foreground' : ''}`}
                onClick={() => setFormData({ ...formData, has_website: true })}
                data-testid="natural-has-website-yes"
              >
                Sí
              </Button>
            </div>
            {formData.has_website && (
              <Input
                type="url"
                placeholder="https://..."
                value={formData.website_url}
                onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                className={`${inputClass} mt-2`}
                data-testid="natural-website-url-input"
              />
            )}
          </div>
        </div>
      </div>

      {/* Características Especiales */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-primary">Características Especiales</h3>
        
        <div className="space-y-4">
          {/* Persona migrante */}
          <div className="space-y-2">
            <Label className={labelClass}>¿Usted es una persona migrante?</Label>
            <div className="flex gap-3">
              <Button
                type="button"
                variant={formData.is_migrant === false ? "default" : "outline"}
                className={`flex-1 h-11 rounded-sm ${formData.is_migrant === false ? 'bg-primary text-primary-foreground' : ''}`}
                onClick={() => setFormData({ ...formData, is_migrant: false, migrant_country: '' })}
              >
                No
              </Button>
              <Button
                type="button"
                variant={formData.is_migrant === true ? "default" : "outline"}
                className={`flex-1 h-11 rounded-sm ${formData.is_migrant === true ? 'bg-primary text-primary-foreground' : ''}`}
                onClick={() => setFormData({ ...formData, is_migrant: true })}
              >
                Sí
              </Button>
            </div>
          </div>

          {/* País Migrante - Solo si es migrante */}
          {formData.is_migrant && (
            <div className="space-y-2">
              <Label htmlFor="migrant_country" className={labelClass}>
                País Migrante *
              </Label>
              <Select
                value={formData.migrant_country}
                onValueChange={(value) => setFormData({ ...formData, migrant_country: value })}
                required
              >
                <SelectTrigger className={selectTriggerClass}>
                  <SelectValue placeholder="Seleccione país de origen..." />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {countries.map((country) => (
                    <SelectItem key={country} value={country}>
                      {country}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* LGBTIQ+ */}
          <div className="space-y-2">
            <Label htmlFor="is_lgbtiq" className={labelClass}>
              ¿Usted hace parte de los sectores sociales LGBTIQ+ y/o personas que se reconocen con orientaciones sexuales e identidades de género diversas?
            </Label>
            <Select
              value={formData.is_lgbtiq}
              onValueChange={(value) => setFormData({ ...formData, is_lgbtiq: value })}
            >
              <SelectTrigger className={selectTriggerClass}>
                <SelectValue placeholder="Seleccione..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no">No</SelectItem>
                <SelectItem value="si">Sí</SelectItem>
                <SelectItem value="prefiero_no_decir">Prefiero no decirlo</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Trabajos de cuidado */}
          <div className="space-y-2">
            <Label className={labelClass}>¿Considera usted que realiza trabajos de cuidado directo o indirecto, remunerados o no remunerado?</Label>
            <div className="flex gap-3">
              <Button
                type="button"
                variant={formData.is_caregiver === false ? "default" : "outline"}
                className={`flex-1 h-11 rounded-sm ${formData.is_caregiver === false ? 'bg-primary text-primary-foreground' : ''}`}
                onClick={() => setFormData({ ...formData, is_caregiver: false })}
              >
                No
              </Button>
              <Button
                type="button"
                variant={formData.is_caregiver === true ? "default" : "outline"}
                className={`flex-1 h-11 rounded-sm ${formData.is_caregiver === true ? 'bg-primary text-primary-foreground' : ''}`}
                onClick={() => setFormData({ ...formData, is_caregiver: true })}
              >
                Sí
              </Button>
            </div>
          </div>

          {/* Veterano */}
          <div className="space-y-2">
            <Label className={labelClass}>¿Es usted un Veterano de la Fuerza Pública o un beneficiario de Veterano de la Fuerza Pública?</Label>
            <div className="flex gap-3">
              <Button
                type="button"
                variant={formData.is_veteran === false ? "default" : "outline"}
                className={`flex-1 h-11 rounded-sm ${formData.is_veteran === false ? 'bg-primary text-primary-foreground' : ''}`}
                onClick={() => setFormData({ ...formData, is_veteran: false })}
              >
                No
              </Button>
              <Button
                type="button"
                variant={formData.is_veteran === true ? "default" : "outline"}
                className={`flex-1 h-11 rounded-sm ${formData.is_veteran === true ? 'bg-primary text-primary-foreground' : ''}`}
                onClick={() => setFormData({ ...formData, is_veteran: true })}
              >
                Sí
              </Button>
            </div>
          </div>

          {/* Rango de edad 14-28 (automático) */}
          <div className="space-y-2">
            <Label className={labelClass}>Persona entre 14 y 28 años</Label>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                disabled
                className={`flex-1 h-11 rounded-sm ${formData.is_youth_14_28 ? 'bg-green-100 border-green-500 text-green-700' : 'bg-gray-100'}`}
              >
                {formData.is_youth_14_28 ? 'Sí (calculado automáticamente)' : 'No (calculado automáticamente)'}
              </Button>
            </div>
          </div>

          {/* Rango de edad 60+ (automático) */}
          <div className="space-y-2">
            <Label className={labelClass}>Persona de 60 años o más</Label>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                disabled
                className={`flex-1 h-11 rounded-sm ${formData.is_senior_60_plus ? 'bg-green-100 border-green-500 text-green-700' : 'bg-gray-100'}`}
              >
                {formData.is_senior_60_plus ? 'Sí (calculado automáticamente)' : 'No (calculado automáticamente)'}
              </Button>
            </div>
          </div>

          {/* Discapacidad */}
          <div className="space-y-2">
            <Label className={labelClass}>¿Tiene alguna condición de discapacidad?</Label>
            <div className="flex gap-3">
              <Button
                type="button"
                variant={formData.has_disability === false ? "default" : "outline"}
                className={`flex-1 h-11 rounded-sm ${formData.has_disability === false ? 'bg-primary text-primary-foreground' : ''}`}
                onClick={() => setFormData({ ...formData, has_disability: false, disability_type: '' })}
              >
                No
              </Button>
              <Button
                type="button"
                variant={formData.has_disability === true ? "default" : "outline"}
                className={`flex-1 h-11 rounded-sm ${formData.has_disability === true ? 'bg-primary text-primary-foreground' : ''}`}
                onClick={() => setFormData({ ...formData, has_disability: true })}
              >
                Sí
              </Button>
            </div>
            {formData.has_disability && (
              <div className="space-y-2 pt-2">
                <Label htmlFor="disability_type" className={labelClass}>
                  Tipo de discapacidad *
                </Label>
                <Select
                  value={formData.disability_type}
                  onValueChange={(value) => setFormData({ ...formData, disability_type: value })}
                >
                  <SelectTrigger className={selectTriggerClass} data-testid="register-disability-type-select">
                    <SelectValue placeholder="Seleccione..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auditiva">Auditiva</SelectItem>
                    <SelectItem value="visual">Visual</SelectItem>
                    <SelectItem value="fisica">Física</SelectItem>
                    <SelectItem value="sordoceguera">Sordoceguera</SelectItem>
                    <SelectItem value="intelectual">Intelectual</SelectItem>
                    <SelectItem value="psicosocial">Psicosocial</SelectItem>
                    <SelectItem value="multiple">Múltiple</SelectItem>
                    <SelectItem value="otro">Otro</SelectItem>
                    <SelectItem value="ninguna">Ninguna</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Población Vulnerable */}
          <div className="space-y-2">
            <Label className={labelClass}>¿Eres población vulnerable?</Label>
            <div className="flex gap-3">
              <Button
                type="button"
                data-testid="vulnerable-population-no"
                variant={formData.vulnerable_population === false ? "default" : "outline"}
                className={`flex-1 h-11 rounded-sm ${formData.vulnerable_population === false ? 'bg-primary text-primary-foreground' : ''}`}
                onClick={() => setFormData({ ...formData, vulnerable_population: false, vulnerable_population_type: '' })}
              >
                No
              </Button>
              <Button
                type="button"
                data-testid="vulnerable-population-yes"
                variant={formData.vulnerable_population === true ? "default" : "outline"}
                className={`flex-1 h-11 rounded-sm ${formData.vulnerable_population === true ? 'bg-primary text-primary-foreground' : ''}`}
                onClick={() => setFormData({ ...formData, vulnerable_population: true })}
              >
                Sí
              </Button>
            </div>
            {formData.vulnerable_population && (
              <div className="space-y-2 pt-2">
                <Label htmlFor="vulnerable_population_type" className={labelClass}>Tipo de población vulnerable *</Label>
                <Select
                  value={formData.vulnerable_population_type}
                  onValueChange={(value) => setFormData({ ...formData, vulnerable_population_type: value })}
                >
                  <SelectTrigger className={selectTriggerClass} data-testid="natural-vulnerable-type-select">
                    <SelectValue placeholder="Seleccione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {VULNERABLE_POPULATION_TYPES.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Víctima con RUV */}
          <div className="space-y-2">
            <Label className={labelClass}>¿Eres víctima con RUV?</Label>
            <div className="flex gap-3">
              <Button
                type="button"
                data-testid="victim-ruv-no"
                variant={formData.victim_ruv === false ? "default" : "outline"}
                className={`flex-1 h-11 rounded-sm ${formData.victim_ruv === false ? 'bg-primary text-primary-foreground' : ''}`}
                onClick={() => setFormData({ ...formData, victim_ruv: false })}
              >
                No
              </Button>
              <Button
                type="button"
                data-testid="victim-ruv-yes"
                variant={formData.victim_ruv === true ? "default" : "outline"}
                className={`flex-1 h-11 rounded-sm ${formData.victim_ruv === true ? 'bg-primary text-primary-foreground' : ''}`}
                onClick={() => setFormData({ ...formData, victim_ruv: true })}
              >
                Sí
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Me Interesa (Áreas de Interés) */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-primary">Me Interesa</h3>
        <p className="text-xs text-muted-foreground">Seleccione las áreas en las que está interesado (puede elegir varias)</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            'Asesorias especializadas',
            'Contactos',
            'Desarrollar nuevos productos o servicios',
            'Financiacion',
            'Informacion general de iNNpulsa',
            'Participar en eventos',
            'Procesos de internacionalizacion de productos',
            'Socios inversionistas'
          ].map((option) => {
            const checked = (formData.interests || []).includes(option);
            return (
              <label
                key={option}
                className={`flex items-center gap-3 p-3 border rounded-sm cursor-pointer transition-all ${
                  checked
                    ? 'border-primary bg-primary/10'
                    : darkMode ? 'border-gray-700 hover:border-gray-500' : 'border-gray-300 hover:border-gray-400'
                }`}
                data-testid={`natural-interest-${option.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => {
                    setFormData((prev) => {
                      const current = prev.interests || [];
                      const next = current.includes(option)
                        ? current.filter((i) => i !== option)
                        : [...current, option];
                      return { ...prev, interests: next };
                    });
                  }}
                />
                <span className="text-sm">{option}</span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Contraseña y Autorización */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-primary">Seguridad y Autorización</h3>
        
        <div className="space-y-2">
          <Label htmlFor="password" className={labelClass}>
            Contraseña *
          </Label>
          <div className="relative">
            <Lock className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="pl-10 h-11 rounded-sm"
              required
              minLength={6}
              data-testid="register-password-input"
            />
          </div>
          <p className="text-xs text-muted-foreground">Mínimo 6 caracteres</p>
        </div>

        <div className="flex items-start space-x-2 p-4 bg-secondary/50 rounded-sm border border-border">
          <Checkbox
            id="data_authorization"
            checked={formData.data_authorization}
            onCheckedChange={(checked) => setFormData({ ...formData, data_authorization: checked })}
            required
          />
          <Label htmlFor="data_authorization" className="text-sm cursor-pointer leading-relaxed">
            <strong>AUTORIZACIÓN PARA EL TRATAMIENTO DE DATOS PERSONALES</strong><br /><br />
            Autorizo de manera previa, expresa, informada e inequívoca a <strong>iNNpulsa Colombia</strong>, para que, de conformidad con la Ley 1581 de 2012 y demás normas aplicables en materia de protección de datos personales, realice el tratamiento de mis datos personales mediante su recolección, almacenamiento, uso, circulación, supresión, transmisión y transferencia, de acuerdo con las siguientes finalidades: enviarme información sobre sus instrumentos, invitaciones, capacitaciones, programas, eventos y demás actividades que puedan fortalecer y potencializar mi quehacer y contribuir a la superación de mis desafíos; las demás finalidades incluidas en la política de protección de datos; así como compartir mi información con aliados, operadores y proveedores, cuando ello sea necesario para el desarrollo de las actividades y demás finalidades previamente informadas y autorizadas.<br /><br />
            Como titular de los datos personales, tengo derecho a conocer, acceder, actualizar y rectificar mi información, presentar consultas y reclamos; acceder gratuitamente a mis datos personales y, cuando sea procedente, solicitar su supresión o revocar la autorización.<br /><br />
            <em>*Para conocer la Política de Tratamiento de Datos Personales de iNNpulsa Colombia, así como las finalidades del tratamiento, los procedimientos y canales habilitados para presentar consultas, solicitudes o reclamos, y los derechos que me asisten como titular de los datos, puede consultarla en:{' '}
            <a href="https://www.innpulsacolombia.com/transparencia/" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
              https://www.innpulsacolombia.com/transparencia/
            </a>.</em> *
          </Label>
        </div>
      </div>

      <Button
        type="submit"
        disabled={loading}
        className="w-full h-11 text-white font-medium transition-all active:scale-95 rounded-xl"
        style={{ background: 'linear-gradient(135deg, #9B1427 0%, #C41E3A 100%)' }}
        data-testid="register-submit-button"
      >
        {loading ? 'Creando cuenta...' : 'Crear cuenta'}
      </Button>
      </form>
    </div>
  );
}
